import { useCallback, useEffect, useRef, useState } from 'react';
import { TextInput, tokens } from '@hotjar/hotsauce';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { GoogleSSOButton } from '@hotjar/webapp/components/SSOButton';
import { TextHorizontalRule } from '@hotjar/webapp/components/TextHorizontalRule';
import { PasswordInputWithValidation } from '@hotjar/webapp/components/PasswordInputWithValidation';
import { css } from '@emotion/react';
import { validateEmail } from '@hotjar/webapp/lib/email';
import { useMeasureRenderFromNavigationStart } from '@hotjar/performance';
// eslint-disable-next-line import/no-extraneous-dependencies
import ReCAPTCHA from 'react-google-recaptcha';
import { useGoogleSsoSignUp } from '@hotjar/webapp/features/Registration/hooks/useGoogleSsoSignUp';
import { useQueryParameter } from '@hotjar/webapp/hooks/useQueryParameter';
import { segment } from '@hotjar/tracking';
import { setEmailErrorByReason } from './utils';
import {
  ColFormWrapper,
  Form,
  StyledAlert,
  StyledButton,
  StyledCheckbox,
  StyledErrorText,
  StyledField,
  TermsOfServiceLink,
  Title,
} from '../../common/styles';

import { SignInText, StyledLink } from './styles';

interface ReCAPTCHARef {
  getValue: () => string | null;
}

export interface SignupFormData {
  name: string;
  email: string;
  password: string;
  terms: string;
  captcha?: string;
}

export type SubmitUserFormData = (
  formData: SignupFormData,
) => Promise<{ vid?: string; reasons?: string[] }>;

export type SelectSignUpMethodStepProps = {
  recaptchaValue: string | null;
  setRecaptchaValue: (c: string | null) => void;
  isCaptchaVisible: boolean;
  setIsCaptchaVisible: (v: boolean) => void;
  submitUserFormData: SubmitUserFormData;
  selectEmailMethod: () => void;
  shouldShowEmailForm: boolean;
  isCampaignValid: boolean;
  isStepOneSubmitInProgress: boolean;
  defaultData?: SignupFormData;
};

export const SelectSignUpMethodStep = ({
  recaptchaValue,
  setRecaptchaValue,
  isCaptchaVisible,
  setIsCaptchaVisible,
  submitUserFormData,
  shouldShowEmailForm,
  isCampaignValid,
  defaultData = {} as SignupFormData,
  isStepOneSubmitInProgress,
  selectEmailMethod,
}: SelectSignUpMethodStepProps): JSX.Element => {
  // Homepage Experiment query param from ACQ-1686
  const prefillEmail = useQueryParameter('prefill_email');
  const recaptchaRef = useRef(null);
  const { t } = useTranslation();
  const { googleSsoSignUpClick } = useGoogleSsoSignUp();
  const { register, handleSubmit, errors, setError, clearErrors, formState } =
    useForm<SignupFormData>({
      mode: 'onSubmit',
      reValidateMode: 'onChange',
      defaultValues: defaultData,
    });
  const [isPasswordValid, setIsPasswordValid] = useState(true);

  const [agreedToTermsAndConditions, setAgreedToTermsAndConditions] = useState(
    defaultData.terms === 'true',
  );
  const horizontalRuleMessage =
    shouldShowEmailForm && !prefillEmail
      ? t('common:registration.select-signup-method.horizontal-rule-msg-one')
      : t('common:registration.select-signup-method.horizontal-rule-msg-two');

  const onSubmit = (formData: SignupFormData) => {
    if (!isPasswordValid) {
      setError('password', {
        type: 'manual',
        message: 'Invalid password',
      });
    } else {
      clearErrors('password');
      submitUserFormData(formData)
        .then(() => {
          setIsCaptchaVisible(false);
        })
        .catch((err) => {
          const { reasons, message } = err;
          if (message === 'captcha required') {
            setIsCaptchaVisible(true);
            return setError('captcha', {
              type: 'custom',
              message: t('common:registration.validation-errors.captcha-required'),
            });
          }
          const error = t(setEmailErrorByReason(reasons));
          return setError('email', {
            type: 'manual',
            message: error,
          });
        });
    }
  };

  useEffect(() => {
    if (!recaptchaValue) return;
    clearErrors('captcha');
  }, [recaptchaValue, clearErrors]);

  useEffect(() => {
    if (!isPasswordValid) {
      setError('password', {
        type: 'manual',
        message: 'Invalid password',
      });
    } else {
      clearErrors('password');
    }
  }, [isPasswordValid, setError, clearErrors]);

  useMeasureRenderFromNavigationStart(
    'registration-form',
    useCallback(({ renderTime, loadingType }: { renderTime: number; loadingType: string }) => {
      let performanceBucket = null;
      if (renderTime <= 2500) {
        performanceBucket = 'good';
      } else if (renderTime > 4000) {
        performanceBucket = 'bad';
      } else {
        performanceBucket = 'average';
      }

      segment.track('Perceived Loading Performance Measured', {
        loading_type: loadingType,
        performance_bucket: performanceBucket,
        view_name: 'Signup From',
      });
    }, []),
  );

  return (
    <ColFormWrapper>
      <Form
        id="step1"
        data-testid="step1"
        name="step1"
        autoComplete="off"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        {!isCampaignValid && (
          <StyledAlert kind="warning" id="trial-error" data-testid="campaign-error">
            {t('common:registration.select-signup-method.invalid-campaign')}
          </StyledAlert>
        )}
        <Title>{t('common:registration.select-signup-method.create-account')}</Title>
        {!prefillEmail && (
          <>
            <GoogleSSOButton
              id="google-button"
              data-testid="google-button"
              onClick={googleSsoSignUpClick}
            >
              {t('common:registration.select-signup-method.google-signup')}
            </GoogleSSOButton>
            <TextHorizontalRule
              message={horizontalRuleMessage}
              css={css`
                margin: ${shouldShowEmailForm ? tokens.spacer.x3 : tokens.spacer.x4} 0;
              `}
            />
          </>
        )}
        {shouldShowEmailForm || prefillEmail ? (
          <div data-testid="signup-with-email">
            {prefillEmail ? (
              <>
                <StyledField
                  size="l"
                  inputId="email"
                  isInvalid={!!errors.email}
                  invalidText={errors.email?.message}
                  labelText={t('common:registration.select-signup-method.email-field-label')}
                >
                  <TextInput
                    size="l"
                    id="email"
                    name="email"
                    data-testid="email-input"
                    maxLength={100}
                    isRequired
                    isInvalid={!!errors.email}
                    ref={register({
                      required: t('common:registration.validation-errors.required') as string,
                      validate: (value: string) =>
                        validateEmail(value) ||
                        (t('common:registration.validation-errors.email_restricted') as string),
                    })}
                    placeholder={t(
                      'common:registration.select-signup-method.email-field-placeholder',
                    )}
                    defaultValue={prefillEmail}
                  />
                </StyledField>
                <StyledField
                  size="l"
                  inputId="name"
                  isInvalid={!!errors.name}
                  invalidText={errors.name?.message}
                  labelText={t('common:registration.select-signup-method.name-field-label')}
                >
                  <TextInput
                    size="l"
                    id="name"
                    name="name"
                    autoFocus
                    data-testid="name-input"
                    placeholder={t(
                      'common:registration.select-signup-method.name-field-placeholder',
                    )}
                    maxLength={50}
                    isRequired
                    ref={register({
                      required: t('common:registration.validation-errors.required') as string,
                    })}
                    isInvalid={!!errors.name}
                  />
                </StyledField>
              </>
            ) : (
              <>
                <StyledField
                  size="l"
                  inputId="name"
                  isInvalid={!!errors.name}
                  invalidText={errors.name?.message}
                  labelText={t('common:registration.select-signup-method.name-field-label')}
                >
                  <TextInput
                    size="l"
                    id="name"
                    name="name"
                    autoFocus
                    data-testid="name-input"
                    placeholder={t(
                      'common:registration.select-signup-method.name-field-placeholder',
                    )}
                    maxLength={50}
                    isRequired
                    ref={register({
                      required: t('common:registration.validation-errors.required') as string,
                    })}
                    isInvalid={!!errors.name}
                  />
                </StyledField>
                <StyledField
                  size="l"
                  inputId="email"
                  isInvalid={!!errors.email}
                  invalidText={errors.email?.message}
                  labelText={t('common:registration.select-signup-method.email-field-label')}
                >
                  <TextInput
                    size="l"
                    id="email"
                    name="email"
                    data-testid="email-input"
                    maxLength={100}
                    isRequired
                    isInvalid={!!errors.email}
                    ref={register({
                      required: t('common:registration.validation-errors.required') as string,
                      validate: (value: string) =>
                        validateEmail(value) ||
                        (t('common:registration.validation-errors.email_restricted') as string),
                    })}
                    placeholder={t(
                      'common:registration.select-signup-method.email-field-placeholder',
                    )}
                    defaultValue={prefillEmail}
                  />
                </StyledField>
              </>
            )}

            <PasswordInputWithValidation
              ref={register({ required: true })}
              defaultValue={defaultData?.password}
              required={formState.isSubmitted}
              isPasswordValidCallback={(isValid) => setIsPasswordValid(!!isValid)}
            />
            {isCaptchaVisible && (
              <>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: tokens.spacer.x3,
                  }}
                >
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey="6LcPBRkTAAAAAGSAE3YmUbwn7z_o-AUAe5eNqOsu"
                    onChange={() => {
                      if (!recaptchaRef.current) return;
                      setRecaptchaValue((recaptchaRef.current as ReCAPTCHARef)?.getValue());
                    }}
                  />
                </div>
                {errors.captcha && <StyledErrorText>{errors.captcha?.message}</StyledErrorText>}
              </>
            )}

            <StyledCheckbox
              id="terms"
              name="terms"
              value="true"
              ref={register({
                required: t('common:registration.validation-errors.accept_terms') as string,
              })}
              isChecked={agreedToTermsAndConditions}
              onChange={() => setAgreedToTermsAndConditions(!agreedToTermsAndConditions)}
              isInvalid={!!errors.terms}
              labelContent={
                <span id="termsLabel" data-testid="terms-link">
                  {t('common:registration.select-signup-method.terms-label')}
                  <TermsOfServiceLink
                    href="https://www.hotjar.com/legal/policies/terms-of-service/"
                    kind="inline"
                    isExternal
                  >
                    {t('common:registration.select-signup-method.terms-link')}
                  </TermsOfServiceLink>
                </span>
              }
            />
            {errors.terms && <StyledErrorText>{errors.terms.message}</StyledErrorText>}
            <StyledButton
              id="submit-step-1"
              data-testid="submit-step-1"
              type="submit"
              kind="primary"
              isDisabled={isStepOneSubmitInProgress}
              isLoading={isStepOneSubmitInProgress}
              size="l"
              loadingText={t('common:registration.select-signup-method.submit-btn-loading')}
            >
              {t('common:registration.select-signup-method.submit-btn-text')}
            </StyledButton>

            {prefillEmail && (
              <>
                <TextHorizontalRule
                  message={horizontalRuleMessage}
                  css={css`
                    margin: ${shouldShowEmailForm ? tokens.spacer.x3 : tokens.spacer.x4} 0;
                  `}
                />
                <GoogleSSOButton
                  id="google-button"
                  data-testid="google-button"
                  onClick={googleSsoSignUpClick}
                >
                  {t('common:registration.select-signup-method.google-signup')}
                </GoogleSSOButton>
              </>
            )}
          </div>
        ) : (
          <StyledButton
            id="show-email-fields"
            data-testid="show-email-fields"
            kind="primary"
            size="l"
            onClick={selectEmailMethod}
          >
            {t('common:registration.select-signup-method.email-signup')}
          </StyledButton>
        )}
        <SignInText>
          {t('common:registration.select-signup-method.already-have-account')}
          <StyledLink href="/login" kind="standalone">
            {t('common:registration.select-signup-method.signin-now')}
          </StyledLink>
        </SignInText>
      </Form>
    </ColFormWrapper>
  );
};
